import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import WebViewer, { WebViewerInstance, Core } from "@pdftron/webviewer";
import { connect } from "react-redux";
import AddReviewEntityPopup from "./AddReviewEntityPopup";
import {
    createQAReviewEntityCreator,
    qaReviewEntityProps,
    getReviewEntitiesActionCreator,
} from "../../components/dataEntryDetails/DataEntryDetailsDucks";
import Preferences from '../../models/preferences';
import { DocTypes } from '../constants/DocTypes';
import { getQAFeatureFlagsRequest } from "../../components/login/LoginPageDucks";
import TransactionDocument from '../../models/document';
import { AssessmentResult, EntityType, EntityTypeId } from "../../models/assessment";
import { hexToRgb } from './annotations';
import { storedBoundsToPdfTronQuad } from './pdfTronCoords';
import { FieldGrounding } from '../../models/intake';

interface ParentProps {
    fileData?: TransactionDocument & { filePath: string },
    saveTrigger?: boolean,
    mergeTrigger?: boolean,
    getFileData?: (data: TransactionDocument & { blobData: Blob, pagesDeleted: number }) => void,
    canSave?: boolean,
    deleteFile?: Function,
    pageNumber: number,
    transactionId: string,
    refresh?: boolean,
    /**
     * Assessment result of the currently open document.
     *
     * Should not be set unless the assessed document is the one displayed.
     */
    assessmentResult?: AssessmentResult,
    setPageNumber?: Function,
    currentFile?: string,
    annotationAllowed?: boolean,
    getAuditTrailData: (transactionId: string) => void,
    authorizedEntities: { readonly [key in EntityTypeId]?: boolean },
}

interface StateProps {
    license: string,
    entityTypes: readonly EntityType[],
    preferences: Preferences,
    // AB#31605 (Quick Entry grounding highlight): the field whose source region to
    // highlight on-click, or undefined for none. Read straight from redux so it needs
    // no prop threading through DataEntryDetailsBody. Set via setGroundingHighlightActionCreator.
    groundingHighlight?: FieldGrounding,
}

interface DispatchProps {
    createQAReviewEntity: (data: qaReviewEntityProps) => void,
    getReviewEntities: (transactionId: string) => void,
    getQAFeatureFlagsRequest?: Function,
}

function PdfViewer(props: ParentProps & StateProps & DispatchProps) {
    const viewerDiv = useRef<HTMLDivElement | null>(null)
    const [viewerInstance, setViewerInstance] = useState<WebViewerInstance | undefined>()
    const [originalPageCount, setOriginalPageCount] = useState(0);
    const [showReviewEntityForm, setShowReviewEntityForm] = useState(false);
    const [annotation, setAnnotation] = useState<qaReviewEntityProps | undefined>();

    const { preferences, assessmentResult, annotationAllowed, authorizedEntities } = props;
    const colors = viewerInstance && props.entityTypes.reduce((prev, curr) => {
        const { r, g, b, a } = hexToRgb(curr.color);
        return {
            ...prev,
            [curr.id]: new viewerInstance.Core.Annotations.Color(r, g, b, a / 255),
        }
    }, {});

    const mergeFileInput = useRef<any>(null)

    useLayoutEffect(() => {
        if (props.getQAFeatureFlagsRequest) {
          props.getQAFeatureFlagsRequest();
        }
      }, [])

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        viewerInstance?.Core.createDocument(e.target.files![0]).then(doc => {
            const sourcePages = viewerInstance.Core.documentViewer.getDocument().getPageCount();
            viewerInstance.Core.documentViewer.getDocument().insertPages(doc, undefined, sourcePages + 1);
        })
    }

    const toggleReviewEntityForm = () => {
        setShowReviewEntityForm(!showReviewEntityForm)
    }

    function onQaEntitySubmitted(data: qaReviewEntityProps) {
        if (viewerInstance) {
            const annotations = viewerInstance.Core.annotationManager
                .getAnnotationsList()
                .filter(ann => ann.Author == "Guest");
            viewerInstance.Core.annotationManager.deleteAnnotations(annotations);
        }
        props.createQAReviewEntity(data);
    }

    function OnAddEntityPressed(currentInstance: WebViewerInstance) {
        const { annotationManager } = currentInstance.Core;
        const annotation = annotationManager.getSelectedAnnotations()[0];

        if (assessmentResult && props.annotationAllowed && props.fileData) {
            if (annotation.Author == "Guest") {
                const rect = annotation.getRect();
                const data: qaReviewEntityProps = {
                    docAIProcessId: assessmentResult.processId,
                    typeId: "none",
                    documentFilename: props.fileData.file.name,
                    page: annotation.getPageNumber() - 1,
                    x1: rect.x1,
                    y1: rect.y1,
                    x2: rect.x2,
                    y2: rect.y2,
                    highlightText: annotation.getCustomData('trn-annot-preview'),
                    organizationID: Number(props.transactionId.substring(0, props.transactionId.indexOf("-"))),
                    requestBufferID: Number(props.transactionId.substring(props.transactionId.indexOf("-") + 1, props.transactionId.length)),
                    userId: global.userID
                }
                setAnnotation(data);
                toggleReviewEntityForm();
            } else {
                alert("This annotation is already marked.");
            }
        } else {
            annotationManager.deleteAnnotations([annotation]);
            alert("Entities may only be marked on a release document with assessment results.");
        }
    }

    // Refs to keep track of the latest props
    const currentPageRef = useRef(props.pageNumber);
    const currentFileRef = useRef(props.currentFile);

    // Keep refs updated whenever props change
    useEffect(() => {
        currentPageRef.current = props.pageNumber;
    }, [props.pageNumber]);

    useEffect(() => {
        currentFileRef.current = props.currentFile;
    }, [props.currentFile]);

    useEffect(() => {
        if (props.saveTrigger && props.fileData && viewerInstance) {
            const getFileData = async () => {
                const { documentViewer, annotationManager } = viewerInstance.Core;
                const doc = documentViewer.getDocument();
                const xfdfString = await annotationManager.exportAnnotations();
                const data = await doc.getFileData({ xfdfString });
                const arr = new Uint8Array(data);
                return new Blob([arr], { type: "application/pdf" });
            };

            const pagesRemoved = originalPageCount - (viewerInstance?.Core.documentViewer.getPageCount() || 0);

            getFileData().then(blob => {
                props.getFileData?.({
                    ...props.fileData!,
                    blobData: blob,
                    pagesDeleted: pagesRemoved
                });
            });
        }
    }, [props.saveTrigger])

    useEffect(() => {
        if (props.mergeTrigger) {
            mergeFileInput.current?.click();
        }
    }, [props.mergeTrigger]);

    useEffect(() => {
        if (!props.license) {
            return;
        }

        if (props.fileData?.filePath) {
           if (viewerInstance) {
                const fixedPath = props.fileData.filePath.replace(/^\./, '')
                viewerInstance.UI.loadDocument(fixedPath, { extension: 'pdf' });
                if (props.fileData.type.role == DocTypes.RELEASE || props.fileData.type.role == DocTypes.QA) {
                    viewerInstance.UI.disableElements(['thumbDelete', 'deletePage']);
                } else {
                    viewerInstance.UI.enableElements(['thumbDelete', 'deletePage']);
                }

                props.getAuditTrailData(props.transactionId);
            } else {
                WebViewer({
                    // Relative path → resolves to /lib/pdftron at the site root, where the
                    // app (SPA + WebViewer lib) is deployed and served with the .res/.mem
                    // MIME fix. AB#34594.
                    path: 'lib/pdftron',
                    initialDoc: props.fileData.filePath,
                    extension: 'pdf',
                    licenseKey: props.license,
                    css: 'assets/css/pdfviewer.css'
                }, viewerDiv.current as HTMLDivElement).then(instance => {
                    instance.UI.annotationPopup.add([{
                        type: "actionButton",
                        img: "icon-tool-pen-highlight",
                        onClick: () => OnAddEntityPressed(instance),
                        title: "Mark Undetected Entity",
                        id: "markSensitiveInfo",
                        dataElement: "markSensitiveInfo",
                    }]);
                    props.getAuditTrailData(props.transactionId);
                    instance.UI.disableElements(['selectToolButton'])

                    instance.UI.disableElements(['pageAdditionalControlsHeader'])
                    instance.UI.disableElements(['moveToTop'])
                    instance.UI.disableElements(['moveToBottom'])

                    instance.UI.disableElements(['pageInsertionHeader'])
                    instance.UI.disableElements(['insertPageAbove'])
                    instance.UI.disableElements(['insertPageBelow'])

                    instance.UI.disableElements(['pageManipulationHeader'])
                    instance.UI.disableElements(['replacePage'])
                    instance.UI.disableElements(['extractPage'])
                    instance.UI.disableElements(['insertPage'])
                    instance.UI.disableElements(['thumbnailsControlInsert'])


                    instance.UI.disableElements(['viewControlsDivider1'])

                    instance.UI.disableElements(['toggleNotesButton'])
                    instance.UI.disableElements(['toolsHeader'])
                    instance.UI.disableElements(['ribbons'])

                    instance.UI.disableElements(['textUnderlineToolButton'])
                    instance.UI.disableElements(['textSquigglyToolButton'])
                    instance.UI.disableElements(['textStrikeoutToolButton'])
                    instance.UI.disableElements(['linkButton'])

                    instance.UI.disableElements(['annotationCommentButton'])
                    instance.UI.disableElements(['annotationStyleEditButton'])
                    instance.UI.disableElements(['linkButton'])

                    instance.UI.disableElements(['stickyToolButton'])
                    instance.UI.disableElements(['freeHandToolButton'])
                    instance.UI.disableElements(['freeHandHighlightToolButton'])
                    instance.UI.disableElements(['freeTextToolButton'])
                    instance.UI.disableElements(['markInsertTextToolButton'])
                    instance.UI.disableElements(['markReplaceTextToolButton'])

                    instance.UI.disableElements(['linkAnnotationUnlinkButton'])
                    instance.UI.disableElements(['colorPalette']);
                    instance.UI.disableElements(['opacitySlider']);

                    if (!props.annotationAllowed) {
                        instance.UI.disableElements(["markSensitiveInfo"]);
                    }
                    if (props.canSave !== undefined && !props.canSave) {
                        instance.UI.disableElements(['thumbDelete'])
                        instance.UI.disableElements(['deletePage'])
                    } else {
                        instance.Core.documentViewer.addEventListener('documentLoaded', () => {
                            setOriginalPageCount(instance.Core.documentViewer.getPageCount());
                            props.getAuditTrailData(props.transactionId);
                        });

                        if (props.deleteFile) {
                            instance.UI.updateElement('thumbDelete', {
                                onClick: () => {
                                    const selectedPages = instance.UI.ThumbnailsPanel.getSelectedPageNumbers();
                                    if (selectedPages.length == 0) {
                                        selectedPages.push(1);
                                    }
                                    const doc = instance.Core.documentViewer.getDocument();
                                    props.deleteFile && props.deleteFile(
                                        selectedPages.length == instance?.Core.documentViewer.getPageCount(),
                                        () => { doc.removePages(selectedPages); }
                                    );
                                }
                            });
                        }
                    }
                    setViewerInstance(instance);

                    const { documentViewer } = instance.Core;
                    const document = instance.UI.iframeWindow.document;

                    instance.UI.setHeaderItems(header => {
                        const pageInfoDiv = document.createElement('div');
                        pageInfoDiv.style.padding = '0.5em';
                        pageInfoDiv.style.fontSize = '14px';
                        pageInfoDiv.style.color = '#000';

                        let totalPages;
                        documentViewer.addEventListener('documentLoaded', () => {
                            totalPages = documentViewer.getPageCount();
                        });

                        const updatePageInfo = () => {
                            let currentPage = documentViewer.getCurrentPage();
                            const pageInput = document.createElement('input');
                            pageInput.type = 'number';
                            pageInput.value = currentPage.toString();
                            pageInput.min = "1";
                            pageInput.max = totalPages;
                            pageInput.style.width = '50px';
                            pageInput.style.textAlign = 'center';

                            pageInput.addEventListener('change', () => {
                                let inputPage = parseInt(pageInput.value);
                                if (inputPage >= 1 && inputPage <= totalPages) {
                                    documentViewer.setCurrentPage(inputPage, false);
                                } else {
                                    pageInput.value = currentPage.toString();
                                }
                            });

                            pageInfoDiv.innerHTML = '';
                            pageInfoDiv.appendChild(pageInput);
                            pageInfoDiv.appendChild(document.createTextNode(` / ${totalPages}`));
                        };

                        documentViewer.addEventListener('documentLoaded', updatePageInfo);
                        documentViewer.addEventListener('pageNumberUpdated', updatePageInfo);

                        header.push({ type: 'customElement', render: () => pageInfoDiv });
                    });
                });
            }
        } else if (viewerInstance) {
            const docViewer = viewerInstance.Core.documentViewer;
            if (docViewer && typeof docViewer.closeDocument === 'function') {
                docViewer.closeDocument();
            }

            setViewerInstance(undefined);
        }
    }, [props.fileData?.filePath, props.refresh, props.license]);

    useEffect(() => {
        if (viewerInstance) {
            if (!preferences.showThumbnails && !preferences.showBookmarks) {
                viewerInstance.UI.disableElements(["leftPanelButton", "leftPanel"]);
            } else {
                viewerInstance.UI.enableElements(["leftPanelButton", "leftPanel"]);
                if (preferences.showThumbnails) {
                    viewerInstance.UI.enableElements(["thumbnailsPanelButton", "thumbnailsPanel"]);
                } else {
                    viewerInstance.UI.disableElements(["thumbnailsPanelButton", "thumbnailsPanel"]);
                }
                if (preferences.showBookmarks) {
                    viewerInstance.UI.enableElements(["outlinesPanelButton", "outlinesPanel"]);
                } else {
                    viewerInstance.UI.disableElements(["outlinesPanelButton", "outlinesPanel"]);
                }
            }
        }
    }, [preferences.showThumbnails, preferences.showBookmarks, viewerInstance]);

    useEffect(() => {
        if (viewerInstance) {
            if (annotationAllowed) {
                viewerInstance.UI.enableElements(["markSensitiveInfo"]);
            } else {
                viewerInstance.UI.disableElements(["markSensitiveInfo"]);
            }
        }
    }, [annotationAllowed, viewerInstance]);

    const documentLoaded = viewerInstance?.Core.documentViewer.getDocument() != null;
     const markedEntities = assessmentResult && colors && [
        ...Object.keys(assessmentResult.patientNames)
            .flatMap(key => assessmentResult.patientNames[key]),
        ...assessmentResult.detectedEntities,
        ...assessmentResult.reviewEntities,
    ].filter(
        ent => {
            // Must have color and not be authorized
            if (!(ent.type in colors) || authorizedEntities[ent.type]) {
                return false;
            }
            
            // If comingling FF is off, exclude commingling type "5B06B99B-73F5-461A-8F81-C8AE31BB5C32"
            const COMINGLING_ENTITY_TYPE_ID: EntityTypeId = "5B86B99B-73F5-461A-8F81-C8AE31BB5C32" as EntityTypeId;

            if (!props.getQAFeatureFlagsRequest && ent.type === COMINGLING_ENTITY_TYPE_ID) {
                return false;
            }
            
            return true;
        }
    );

    useEffect(() => {
        if (viewerInstance) {
            // Needs to reset this or OnAddEntityPressed will read old values
            viewerInstance.UI.updateElement("markSensitiveInfo", {
                onClick: () => OnAddEntityPressed(viewerInstance),
            });

            if (documentLoaded && markedEntities?.length) {
                const annotationManager = viewerInstance.Core.annotationManager;

                const toPdfTronQuad = (ent: any) => {
                    const doc = viewerInstance.Core.documentViewer.getDocument();
                    if (!doc) {
                        console.warn('[PdfViewer] Document not loaded yet, cannot create highlight quad');
                        return null;
                    }

                    const pageInfo = doc.getPageInfo(ent.page + 1);
                    if (!pageInfo) {
                        console.warn('[PdfViewer] Page info not available for page:', ent.page + 1);
                        return null;
                    }

                    return storedBoundsToPdfTronQuad(
                        {
                            leftSide: ent.leftSide,
                            rightSide: ent.rightSide,
                            topSide: ent.topSide,
                            bottomSide: ent.bottomSide,
                        },
                        {
                            width: pageInfo.width,
                            height: pageInfo.height,
                        },
                    );
                };

                const annotations = markedEntities
                    .map(ent => {
                        const quad = toPdfTronQuad(ent);
                        if (!quad) return null; // Skip if pageInfo not available yet

                        return new viewerInstance.Core.Annotations.TextHighlightAnnotation({
                            Id: ent.id,
                            Quads: [quad],
                            PageNumber: ent.page + 1,
                            Author: "firstName" in ent ? `${ent.firstName} ${ent.lastName}` : "",
                            DateCreated: "submitted" in ent ? ent.submitted : undefined,
                            Color: colors![ent.type],
                        });
                    })
                    .filter((ann): ann is Core.Annotations.TextHighlightAnnotation => ann !== null);

                annotationManager.addAnnotations(annotations);
                for (const currentAnnotation of annotations) {
                    annotationManager.redrawAnnotation(currentAnnotation);
                }
                return () => {
                    try {
                        if (viewerInstance?.Core.documentViewer.getDocument()) {
                            viewerInstance.Core.annotationManager.deleteAnnotations(annotations, { force: true });
                        }
                    } catch (error) {
                        console.error(error);
                    }
                };
            }
        }

    }, [markedEntities, documentLoaded, viewerInstance]);

    // Handles setting zoom and current page on document load
    useEffect(() => {
        if (!viewerInstance) return;

        const docViewer = viewerInstance.Core.documentViewer;

        const applyZoom = () => {
            if (preferences.zoomLevel) {
                viewerInstance.UI.setZoomLevel(preferences.zoomLevel);
            }
        };

        // If the document is already loaded, apply immediately
        if (docViewer.getDocument()) {
            applyZoom();
        }

        // Also listen for future loads
        docViewer.addEventListener("documentLoaded", applyZoom);

        return () => {
            docViewer.removeEventListener("documentLoaded", applyZoom);
        };
    }, [viewerInstance, preferences.zoomLevel]);

    // AB#31605 (Quick Entry grounding highlight) — click-to-reveal source-region overlay.
    // Draws a single transient TextHighlight over the located field's bounding box when the
    // grounding icon is clicked (props.groundingHighlight set via SET_GROUNDING_HIGHLIGHT), and
    // clears it when set back to undefined (no-source click) or when a different field is clicked.
    // Reuses the same PDFTron path as the QA-AI sensitive-entity highlights (storedBoundsToPdfTronQuad
    // + TextHighlightAnnotation). Coordinates are the contract's normalized (0..1) bbox
    // (x=Left, y=Top, w=Right-Left, h=Bottom-Top); the shared quad helper auto-detects normalized
    // input. Only the ~5 located date fields carry coordinates in v1.1b, so only they reach here.
    // NOTE: pixel-accuracy of the overlay shares the shared helper's (still-unverified) origin
    // handling — verify against a real extracted doc; a fix there also corrects the QA-AI highlights.
    const GROUNDING_HIGHLIGHT_ID = "qe-grounding-highlight";
    useEffect(() => {
        if (!viewerInstance) return;
        const { annotationManager, documentViewer } = viewerInstance.Core;

        const clearPrior = () => {
            const prior = annotationManager.getAnnotationsList().filter(a => a.Id === GROUNDING_HIGHLIGHT_ID);
            if (prior.length) annotationManager.deleteAnnotations(prior, { force: true });
        };

        const g = props.groundingHighlight;
        if (!g || !documentViewer.getDocument()) {
            clearPrior();
            return;
        }

        const pageInfo = documentViewer.getDocument().getPageInfo(g.pageNumber);
        if (!pageInfo) {
            clearPrior();
            return;
        }

        const quad = storedBoundsToPdfTronQuad(
            { leftSide: g.x, rightSide: g.x + g.width, topSide: g.y, bottomSide: g.y + g.height },
            { width: pageInfo.width, height: pageInfo.height },
        );

        clearPrior();
        const annotation = new viewerInstance.Core.Annotations.TextHighlightAnnotation({
            Id: GROUNDING_HIGHLIGHT_ID,
            Quads: [quad],
            PageNumber: g.pageNumber,
            Color: new viewerInstance.Core.Annotations.Color(255, 213, 0, 0.4),
        });
        annotation.ReadOnly = true;
        annotation.NoResize = true;
        annotationManager.addAnnotation(annotation);
        annotationManager.redrawAnnotation(annotation);
        annotationManager.jumpToAnnotation(annotation);

        return () => {
            try {
                if (documentViewer.getDocument()) clearPrior();
            } catch (error) {
                console.error(error);
            }
        };
    }, [viewerInstance, props.groundingHighlight]);

    // Handles current page on document load
    useEffect(() => {
        if (!viewerInstance || !props.pageNumber) return;

        const docViewer = viewerInstance.Core.documentViewer;

        const applyPageNumber = () => {
            docViewer.setCurrentPage(props.pageNumber, true);
        };

        // If the document is already loaded, apply immediately
        if (docViewer.getDocument()) {
            applyPageNumber();
        }

        // Also listen for future loads
        docViewer.addEventListener("documentLoaded", applyPageNumber);

        return () => {
            docViewer.removeEventListener("documentLoaded", applyPageNumber);
        };
    }, [viewerInstance, props.pageNumber]);

    // Handles scroll event to reset page number
    useEffect(() => {
        if (!viewerInstance) return;

        const scrollContainer = viewerInstance.Core.documentViewer.getScrollViewElement();
        if (!scrollContainer) return;

        const handleScroll = () => {
            if (props.setPageNumber && viewerInstance.Core.documentViewer.getDocument()) {
                props.setPageNumber(null, currentFileRef.current);
            }
        };

        scrollContainer.addEventListener("scroll", handleScroll);

        return () => {
            scrollContainer.removeEventListener("scroll", handleScroll);
        };
    }, [viewerInstance, props.setPageNumber]);

    return (
        <>
            <div className="webviewer" ref={viewerDiv} />
            <div className="file-uploader" style={{ "visibility": "hidden" }}>
                <input type="file" accept="application/pdf" ref={mergeFileInput} onChange={handleFileInput} />
            </div>
            {showReviewEntityForm && annotation && props.assessmentResult && <AddReviewEntityPopup
                setShowReviewEntityForm={setShowReviewEntityForm}
                entityTypes={props.entityTypes.filter(ent => !authorizedEntities[ent.id])}
                createQAReviewEntity={onQaEntitySubmitted}
                annotation={annotation}
                getReviewEntities={props.getReviewEntities}
                transactionId={props.transactionId}
                docAIProcessID={props.assessmentResult.processId}
            />}
        </>
    );
}

function mapStateToProps(state: any): StateProps {
    return {
        license: state.loginState.pdfTronKey,
        entityTypes: state.dataEntryState.getSensitivityTypes,
        preferences: state.loginState.preferences,
        groundingHighlight: state.dataEntryState.groundingHighlight,
    }
}

export function mapDispatchToProps(dispatch: Function): DispatchProps {
    return {
        createQAReviewEntity: (reviewEntity: qaReviewEntityProps) => dispatch(createQAReviewEntityCreator(reviewEntity)),
        getReviewEntities: (transactionId: string) => dispatch(getReviewEntitiesActionCreator(transactionId)),
        getQAFeatureFlagsRequest: () => dispatch(getQAFeatureFlagsRequest()),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(PdfViewer);